import {useSuspenseQuery} from "@apollo/client";
import classNames from "classnames";
import capitalize from "lodash/capitalize";
import {OverlayTrigger, Popover} from "react-bootstrap";
import {FaArrowAltCircleUp} from "react-icons/fa";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {StatusPill} from "@/components/statusPill/StatusPill.tsx";
import {PillStatus} from "@/components/statusPill/StatusPill.types.ts";
import {Table} from "@/components/table/Table.tsx";
import {TextAlignment} from "@/components/table/Table.types.ts";
import {shortDateTimeUser} from "@/lib/format.tsx";
import {usePagination} from "@/lib/hooks/usePagination.ts";
import {CheckGlobalAlertsResponse, GQL_CHECK_GLOBAL_ALERTS} from "@/lib/queries/alerting.ts";
import {StatusPerLocationResponse} from "@/lib/queries/checks.ts";
import {ALERT_STATE_TO_PILL_STATUS, GlobalAlertState} from "@/lib/types/alerting.ts";
import {Service} from "@/lib/types/checks.ts";
import {AlertListDetailsButton} from "@/pages/checks/rta/components/AlertListDetailsButton.tsx";
import {CheckOutput} from "@/pages/checks/rta/components/CheckOutput.tsx";

export interface IndexedGlobalAlertState extends GlobalAlertState {
  index: number;
}

type AlertListProps = {
  service: Service;
  statusPerLocation: StatusPerLocationResponse;
};

export const AlertsList = ({service, statusPerLocation}: AlertListProps) => {
  const {user: currentUser} = useCurrentUserContext();
  const {pagination, setPagination} = usePagination();

  const {
    data: {globalAlertStates},
  } = useSuspenseQuery<CheckGlobalAlertsResponse>(GQL_CHECK_GLOBAL_ALERTS, {
    variables: {
      serviceId: service.id,
      ...pagination,
    },
  });

  const title = statusPerLocation.total > 0 ? "Recent Alerts Per Location" : "Recent Alerts";

  return (
    <>
      <h5 className="heading fw-bold my-4">{title}</h5>
      <Table
        rows={globalAlertStates.items.map(
          (globalAlert, index) => ({...globalAlert, index: index}) as IndexedGlobalAlertState
        )}
        specs={[
          {
            fieldName: "state",
            fieldTitle: "State",
            meta: {columnClassName: "text-center", headerTextAlignment: TextAlignment.Center},
            accessor: (gas: IndexedGlobalAlertState) => {
              const isLastEntry = pagination.offset + gas.index === globalAlertStates.total - 1;
              const isInitialGlobalAlertState = isLastEntry && gas.state_is_up && gas.state_has_changed && !gas.alert;
              const stateWord = gas.state_is_up ? "up" : "down";
              const toolTipText = isInitialGlobalAlertState
                ? "Pending"
                : gas.state_has_changed
                  ? `${capitalize(stateWord)} alert notifications sent.`
                  : `Continued to be in ${stateWord} state`;

              return (
                <>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Popover>
                        <Popover.Body>{toolTipText}</Popover.Body>
                      </Popover>
                    }
                  >
                    <span>
                      {isInitialGlobalAlertState ? (
                        <FaArrowAltCircleUp className="text-success" />
                      ) : (
                        <StatusPill
                          type="incident"
                          variant="textAndIcon"
                          status={gas.state_is_up ? PillStatus.UP : PillStatus.DOWN}
                          className={classNames({
                            "opacity-100": gas.state_has_changed,
                            "opacity-50": !gas.state_has_changed,
                          })}
                        />
                      )}
                    </span>
                  </OverlayTrigger>
                  <AlertListDetailsButton gas={gas} />
                </>
              );
            },
          },
          {
            fieldName: "created_at",
            fieldTitle: "Date",
            accessor: (gas: IndexedGlobalAlertState) => (
              <span>{shortDateTimeUser(gas.created_at.toString(), currentUser!)}</span>
            ),
          },
          {
            fieldName: "location",
            fieldTitle: "Location",
            meta: {columnClassName: "text-center", headerTextAlignment: TextAlignment.Center},
            accessor: (gas: IndexedGlobalAlertState) => (gas.alert ? gas.alert?.monitoring_server?.location : null),
          },
          {
            fieldName: "probe_server",
            fieldTitle: "Probe Server",
            meta: {columnClassName: "text-center", headerTextAlignment: TextAlignment.Center},
            accessor: (gas: IndexedGlobalAlertState) => (gas.alert ? gas.alert?.monitoring_server?.name : null),
          },
          {
            fieldName: "changed_to",
            fieldTitle: "State Changed To",
            meta: {columnClassName: "text-center", headerTextAlignment: TextAlignment.Center},
            accessor: ({alert}: IndexedGlobalAlertState) => (
              <>
                {alert?.state && (
                  <StatusPill type="incident" variant="textAndIcon" status={ALERT_STATE_TO_PILL_STATUS[alert.state]} />
                )}
              </>
            ),
          },
          {
            fieldName: "output",
            fieldTitle: "Details",
            meta: {columnClassName: "text-word-wrap"},
            accessor: (gas: IndexedGlobalAlertState) => (gas.alert ? <CheckOutput output={gas.alert.output} /> : null),
          },
          {
            fieldName: "num_locations_down",
            fieldTitle: "# Locations Down",
            meta: {columnClassName: "text-center", headerTextAlignment: TextAlignment.Center},
          },
        ]}
        pagination={pagination}
        updatePagination={setPagination}
        totalRows={globalAlertStates.total}
      />
    </>
  );
};
